import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Feedback,
  fieldFeedbackContainer,
  requiredLabel,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  type PhotoPickerUploadSource,
  PhotoPicker,
} from '@noths/polaris-design-system-candidate-components';

import * as copy from 'src/components/organisms/Personalisation/constants/copy';
import * as styles from 'src/components/organisms/Personalisation/styles/Personalisation.styles';
import type {
  ImageUploadAttemptPayload,
  ImageUploadErrorPayload,
  RequiredImageOptionImpressionPayload,
  SelectionPayload,
} from 'src/redux/basket/types';
import { requestImageUploadUrls } from 'src/services/basket-api/mutations';
import { uploadFilesToPresignedSlots } from 'src/services/basket-api/utils/uploadToPresignedUrl';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import type { ImageProductOption } from 'src/types/personalisation';
import { imageOptionBounds } from 'src/utils/imageOptionBounds';
import {
  countImagesForImageOptionLimit,
  listHttpsUrlsInImageOptionValue,
} from 'src/utils/imageOptionWireFormat';
import {
  type PhotoRejectionState,
  buildRejectionLines,
  buildRequirementsLines,
} from './photoRequirements';
import * as imageStyles from './ImageOption.styles';

const ACCEPTED_IMAGE_TYPES: ReadonlyArray<string> = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
];

// Dynamic import here (not in the CommonJS design-system package) so Next
// code-splits the ~1.5MB codec, fetched only when a HEIC file is picked.
const loadHeicConverter = () => import('heic2any').then((module) => module.default);
const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;

const REQUIREMENTS_LINES = buildRequirementsLines(
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_UPLOAD_SIZE_BYTES,
);

export interface ImageOptionDispatchProps {
  selectOption: (payload: SelectionPayload) => void;
  trackImageUploadAttempt: (payload: ImageUploadAttemptPayload) => void;
  trackImageUploadError: (payload: ImageUploadErrorPayload) => void;
  trackRequiredImageOptionImpression: (payload: RequiredImageOptionImpressionPayload) => void;
}

export interface ImageOptionProps extends ImageOptionDispatchProps {
  displayValidation: boolean | undefined;
  error: boolean;
  /** Flagged image objectUrls still present in this option's value (server said
   * they're missing in storage). Non-empty → the option is in an error state. */
  erroredUrls: string[];
  forwardedRef: MutableRefObject<HTMLUListElement | null>;
  option: ImageProductOption;
  productCode: number;
  selectedValues?: CatServiceAPIProductOptionValue[];
}

export const ImageOption = ({
  displayValidation,
  error,
  erroredUrls,
  option,
  productCode,
  selectOption,
  selectedValues,
  trackImageUploadAttempt,
  trackImageUploadError,
  trackRequiredImageOptionImpression,
}: ImageOptionProps) => {
  const describedById = `image-option-drawer-desc-${option.id}`;
  const sectionRef = useRef<HTMLElement | null>(null);

  const selected = selectedValues?.[0];
  const wireValue = selected?.name ?? '';
  const isPackedToken = wireValue.trim().startsWith('v1:');
  const imagePreviewUrlsKey = JSON.stringify(selected?.imagePreviewUrls ?? null);

  const urlsFromWire = useMemo(
    () => (isPackedToken ? [] : listHttpsUrlsInImageOptionValue(wireValue)),
    [isPackedToken, wireValue],
  );

  /** HTTPS URLs parsed from wire, else thumbnails from basket (`imagePreviewUrls`) when wire is `v1:`. */
  const previewUrls = useMemo(() => {
    if (urlsFromWire.length > 0) {
      return urlsFromWire;
    }
    const parsed: unknown = JSON.parse(imagePreviewUrlsKey);
    const fromBasket = (
      Array.isArray(parsed) ? (parsed.filter(Boolean) as string[]) : []
    ) as string[];
    return fromBasket;
  }, [imagePreviewUrlsKey, urlsFromWire]);

  /**
   * For basket-edit mode the wire is a list of storage/object URLs that aren't directly
   * renderable; basket-api returns matching CDN thumbnails in `imagePreviewUrls`. Map
   * wire URL → preview URL by index so PhotoPicker can render the thumbnails without us
   * mutating the wire value held in state.
   */
  const previewByWireUrl = useMemo(() => {
    const parsed: unknown = JSON.parse(imagePreviewUrlsKey);
    if (!Array.isArray(parsed) || urlsFromWire.length === 0) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    urlsFromWire.forEach((wireUrl, i) => {
      const preview = parsed[i];
      if (typeof preview === 'string' && preview.trim() !== '') {
        map.set(wireUrl, preview);
      }
    });
    return map;
  }, [imagePreviewUrlsKey, urlsFromWire]);

  const previewFor = useCallback((url: string) => previewByWireUrl.get(url), [previewByWireUrl]);

  const { max, min } = imageOptionBounds(option);
  const currentCount = countImagesForImageOptionLimit(selected);
  const minRequired = option.required ? Math.max(min, 1) : min;
  const isValidSelection =
    currentCount >= minRequired && currentCount <= max && !(displayValidation && error);

  // Required options show the minimum needed; optional ones show the upper limit
  // (only when bounded — `imageOptionBounds` returns MAX_SAFE_INTEGER when unbounded).
  const boundsHint = useMemo(() => {
    if (option.required) {
      return `Minimum ${minRequired} image${minRequired === 1 ? '' : 's'} required`;
    }
    return max > 0 && max < Number.MAX_SAFE_INTEGER
      ? `You can upload up to ${max} image${max === 1 ? '' : 's'} (optional)`
      : null;
  }, [option.required, minRequired, max]);

  const emitUrls = useCallback(
    (urls: string[]) => {
      const value = urls.length === 0 ? '' : JSON.stringify(urls);
      const nextPreviewUrls =
        urls.length === 0 ? undefined : urls.map((url) => previewByWireUrl.get(url) ?? url);
      selectOption({
        optionId: option.id,
        optionValue: {
          customOptionKind: 'image',
          id: option.id,
          imagePreviewUrls: nextPreviewUrls,
          isCustomInput: true,
          name: value,
          priceModifier: null,
        },
        type: 'image-option',
      });
    },
    [option.id, previewByWireUrl, selectOption],
  );

  const requestUploadSlots = useCallback(
    (count: number) => requestImageUploadUrls({ count, optionId: option.id, productCode }),
    [option.id, productCode],
  );

  // Mount-time impression for each required image option. Multiple required
  // image options on a single product fire multiple events — analytics can
  // dedupe by product_code if a single signal per product is preferred.
  useEffect(() => {
    if (option.required) {
      trackRequiredImageOptionImpression({ product_code: String(productCode) });
    }
    // Fire once per mount; option.required / productCode are stable for a
    // mounted PDP, so we intentionally don't react to identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPayload = useCallback(
    ({ source }: { source: PhotoPickerUploadSource }) => ({
      product_code: String(productCode),
      source: source === 'inline' ? ('image option' as const) : ('photo picker drawer' as const),
    }),
    [productCode],
  );

  const handleUploadError = useCallback(
    (info: { source: PhotoPickerUploadSource }) => trackImageUploadError(buildPayload(info)),
    [buildPayload, trackImageUploadError],
  );

  const handleUploadAttempt = useCallback(
    (info: { source: PhotoPickerUploadSource }) => trackImageUploadAttempt(buildPayload(info)),
    [buildPayload, trackImageUploadAttempt],
  );

  // Hide the form-level "required" Feedback as soon as the customer attempts
  // an upload — they're actively addressing it. Reset when the parent
  // re-validates and clears `error`.
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => {
    if (!error) {
      setErrorDismissed(false);
    }
  }, [error]);
  const handlePickAttempt = useCallback(() => setErrorDismissed(true), []);
  const showRequiredError = error && !errorDismissed;

  const [rejection, setRejection] = useState<PhotoRejectionState>({
    oversized: false,
    total: 0,
    wrongType: false,
  });
  const rejectionLines = useMemo(
    () => buildRejectionLines(rejection, ACCEPTED_IMAGE_TYPES, MAX_IMAGE_UPLOAD_SIZE_BYTES),
    [rejection],
  );

  const imageNotFound = erroredUrls.length > 0;
  const erroredUrlSet = useMemo(() => new Set(erroredUrls), [erroredUrls]);

  // Server image error shows unconditionally (the "required" hint is gated on displayValidation).
  const sectionHasError = Boolean(error) || imageNotFound;

  // Focus the flagged option's trigger. `scrollToInvalidField` handles the
  // scroll but only focuses `textarea,input`, which this option has none of.
  useEffect(() => {
    if (imageNotFound) {
      sectionRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  }, [imageNotFound]);

  return (
    <section
      css={styles.selectionContainer}
      data-has-error={sectionHasError}
      data-is-valid={isValidSelection && !imageNotFound}
      ref={sectionRef}
    >
      {option.required && (
        <p
          aria-hidden="true"
          css={requiredLabel}
          data-has-error={error}
          data-is-valid={isValidSelection}
        >
          {copy.REQUIRED_COPY}
        </p>
      )}
      <p css={styles.groupLabel} dangerouslySetInnerHTML={{ __html: option.name }} />
      <span css={visuallyHidden} id={describedById}>
        {copy.IMAGE_CHOOSE_COPY}
      </span>
      <div data-testid={`image-option-photos-${option.id}`}>
        <PhotoPicker
          acceptedFileTypes={ACCEPTED_IMAGE_TYPES}
          describedById={describedById}
          erroredUrls={erroredUrlSet}
          loadHeicConverter={loadHeicConverter}
          max={max}
          maxFileSizeBytes={MAX_IMAGE_UPLOAD_SIZE_BYTES}
          min={minRequired}
          onChange={emitUrls}
          onFilesRejected={setRejection}
          onPickAttempt={handlePickAttempt}
          onUploadAttempt={handleUploadAttempt}
          onUploadError={handleUploadError}
          previewFor={previewFor}
          rejectionLines={rejectionLines}
          requestUploadSlots={requestUploadSlots}
          required={option.required}
          requirementsLines={REQUIREMENTS_LINES}
          uploadFiles={uploadFilesToPresignedSlots}
          value={previewUrls}
        />
      </div>
      <div css={fieldFeedbackContainer}>
        {imageNotFound ? (
          <Feedback error iconAlign="left" name={copy.IMAGE_NOT_FOUND_COPY} />
        ) : (
          showRequiredError && <Feedback error iconAlign="left" name={copy.REQUIRED_IMAGE_COPY} />
        )}
      </div>
      {boundsHint && <p css={imageStyles.minRequiredHint}>{boundsHint}</p>}
      <div css={imageStyles.requirementsList}>
        {REQUIREMENTS_LINES.map((line) => (
          <p css={imageStyles.requirementsItem} key={line}>
            {line}
          </p>
        ))}
      </div>
    </section>
  );
};
